import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import { defineStore } from "pinia";
import { ProxyDirection } from "@/constants/email-proxy";
import { StoreModule } from "@/constants/store.ts";
import { Service } from "@/constants/cloud-apps.ts";
import { EmailAddInsProvider } from "@/constants/emails.ts";
import type {
  EmailProxyDomain,
  EmailProxyToken,
} from "@/_store/email-security/email-proxy.module.ts";

export const emailProviderToIconMap = {
  [EmailAddInsProvider.GMAIL]: Service.G_SUITE,
  [EmailAddInsProvider.OUTLOOK]: Service.OFFICE_365,
};

interface GatewaySettingsState {
  domains: EmailProxyDomain[];
  domainsOutbound: EmailProxyDomain[];
  isProxyActive: boolean;
  proxyStatus: string | null;
  showSkeletonLoader: boolean;
  loading: boolean;
}

const defaultGatewaySettingsState = {
  domains: [],
  domainsOutbound: [],
  isProxyActive: false,
  proxyStatus: null,
  showSkeletonLoader: true,
  loading: false,
};

export const useGatewaySettingsStore = defineStore(StoreModule.GATEWAY_SETTINGS, {
  state: (): GatewaySettingsState => ({ ...defaultGatewaySettingsState }),
  actions: {
    async init(direction: ProxyDirection) {
      this.showSkeletonLoader = true;

      await this.getDomains(direction);

      this.showSkeletonLoader = false;
    },
    async getDomains(direction: ProxyDirection) {
      this.loading = true;

      const request = {
        ...api.emailProxyDomains(),
        params: {
          direction,
        },
      };

      try {
        const domains = await axiosInstance.request(request);

        if (direction === ProxyDirection.OUTBOUND) {
          this.domainsOutbound = domains.data;
        } else {
          this.domains = domains.data;
        }
      } catch (err) {
        console.error(err);
      }

      this.loading = false;
    },
    async addDomain(payload: Partial<EmailProxyDomain>): Promise<EmailProxyDomain | null> {
      this.loading = true;

      const { name } = payload;

      const request = {
        ...api.emailProxyDomains(),
        data: {
          name,
          direction: ProxyDirection.OUTBOUND,
        },
        method: "POST",
      };

      try {
        const addedDomain = await axiosInstance.request(request);

        this.getDomains(ProxyDirection.OUTBOUND);

        return addedDomain.data;
      } catch (err) {
        console.error(err);

        return null;
      } finally {
        this.loading = false;
      }
    },
    async editDomain(payload: EmailProxyDomain) {
      this.loading = true;

      const request = {
        ...api.editEmailProxyDomain(payload.id),
        data: {
          name: payload.name,
          direction: ProxyDirection.OUTBOUND,
        },
        method: "PUT",
      };

      try {
        await axiosInstance.request(request);
        this.getDomains(ProxyDirection.OUTBOUND);
      } catch (err) {
        console.error(err);
        this.loading = false;
        throw err;
      }

      this.loading = false;
    },
    async removeDomain(payload: EmailProxyDomain) {
      this.loading = true;

      const request = {
        ...api.editEmailProxyDomain(payload.id),
        method: "DELETE",
      };

      try {
        await axiosInstance.request(request);

        this.getDomains(ProxyDirection.OUTBOUND);
      } catch (err) {
        console.error(err);
      }

      this.loading = false;
    },
    async testDomain(payload: EmailProxyDomain): Promise<string> {
      this.loading = true;

      const request = {
        ...api.testEmailProxyDomain(payload.id),
      };

      try {
        const { data } = await axiosInstance.request<{ domainVerificationEmail: string }>(request);

        this.getDomains(payload.direction);

        this.loading = false;
        return data.domainVerificationEmail;
      } catch (err) {
        this.loading = false;
        console.error(err);
        return "";
      }
    },
    async completeProxyTest(payload: EmailProxyToken) {
      this.showSkeletonLoader = true;
      const request = {
        ...api.completeEmailProxyTest(payload.proxyDomainId),
      };

      try {
        const response = await axiosInstance.request(request);
        this.showSkeletonLoader = false;
        return response;
      } catch (error) {
        console.error(error);
        this.showSkeletonLoader = false;
        throw error;
      }
    },
    async getEmailProxyStatus(
      { direction }: { direction: ProxyDirection },
      proxyRecipientsDomain?: string
    ) {
      const request = {
        ...api.getEmailProxyStatus(),
        params: {
          direction,
          domainName: proxyRecipientsDomain,
        },
      };

      try {
        const { data } = await axiosInstance.request(request);
        this.isProxyActive = data.isProxyActive;

        if (data.proxyStatus) {
          this.proxyStatus = data.proxyStatus;
        }

        return data;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
  },
});
